import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  TenantWorkspaceApi,
  type VendorProfile,
  type VendorLocation,
  type ServiceArea,
  type CoverageType,
  type FulfillmentMode,
  type CreateVendorLocationRequest,
  type UpdateLocationRequest,
  type Coordinates,
} from '@/modules/tenants/services/workspace-api';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { ErrorState } from '@/shared/feedback';
import {
  FiPlus,
  FiMapPin,
  FiRefreshCw,
  FiAlertCircle,
  FiSend,
  FiTarget,
  FiEdit2,
} from 'react-icons/fi';

/**
 * Locations & branches.
 *
 * A location is a VENDOR-PROFILE resource: it is created under the tenant's
 * business profile, and it is PUBLISHED through that profile. Publication is
 * what makes a location publicly discoverable — a draft location has no
 * public presence at all.
 *
 * A coordinate is not decoration. Discovery places a vendor by its location's
 * point, and a RADIUS coverage is refused outright for a location that has
 * none, so an operator who wants a branch to serve a neighbourhood has to
 * give it a position.
 */

const PUBLICATION_STATUSES: {
  value: 'draft' | 'published' | 'archived';
  label: string;
  tone: 'positive' | 'caution' | 'neutral';
}[] = [
  { value: 'published', label: 'Published', tone: 'positive' },
  { value: 'draft', label: 'Draft', tone: 'neutral' },
  { value: 'archived', label: 'Archived', tone: 'caution' },
];

const COVERAGE_TYPES: { value: CoverageType; label: string }[] = [
  { value: 'postal', label: 'Postal code' },
  { value: 'radius', label: 'Radius around the location' },
];

const FULFILMENT_MODES: { value: FulfillmentMode; label: string }[] = [
  { value: 'at_customer', label: 'Service at the customer' },
  { value: 'delivery', label: 'Delivery' },
];

/** The API refuses a radius coverage above this. */
const MAX_RADIUS_METERS = 100_000;

type LocationFormState = {
  label: string;
  country: string;
  region: string;
  city: string;
  postalCode: string;
  addressLines: string[];
  latitude: string;
  longitude: string;
  timezone: string;
};

const EMPTY_FORM: LocationFormState = {
  label: '',
  country: 'IN',
  region: '',
  city: '',
  postalCode: '',
  addressLines: [''],
  latitude: '',
  longitude: '',
  timezone: 'Asia/Kolkata',
};

/**
 * Reads the coordinate pair out of the form.
 *
 * A location may have no coordinate at all, but it never has half of one.
 */
function readCoordinates(
  form: LocationFormState,
): { coordinates?: Coordinates } | { message: string } {
  const hasLatitude = form.latitude.trim() !== '';
  const hasLongitude = form.longitude.trim() !== '';
  if (hasLatitude !== hasLongitude) {
    return { message: 'Latitude and longitude come as a pair — fill in both, or neither' };
  }
  if (!hasLatitude) {
    return { coordinates: undefined };
  }
  const latitude = Number(form.latitude);
  const longitude = Number(form.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return { message: 'Latitude and longitude must be numbers' };
  }
  return { coordinates: { latitude, longitude } };
}

/**
 * Turns the form into the request the API accepts, or names what is missing.
 *
 * The check lives here rather than in the handler so the handler stays a
 * straight line: read the form, ask for the request, send it.
 */
function buildLocationRequest(
  form: LocationFormState,
): { request: CreateVendorLocationRequest } | { message: string } {
  const addressLines = form.addressLines.map((line) => line.trim()).filter(Boolean);
  if (!form.label.trim()) {
    return { message: 'A label is required' };
  }
  if (addressLines.length === 0) {
    return { message: 'At least one address line is required' };
  }
  if (form.country.trim().length !== 2) {
    return { message: 'Country is a two-letter code' };
  }
  const point = readCoordinates(form);
  if ('message' in point) {
    return point;
  }
  return {
    request: {
      label: form.label.trim(),
      privateAddress: {
        addressLines,
        country: form.country.trim().toUpperCase(),
        region: form.region.trim() || undefined,
        city: form.city.trim() || undefined,
        postalCode: form.postalCode.trim() || undefined,
        coordinates: point.coordinates,
      },
      timezone: form.timezone.trim() || 'UTC',
    },
  };
}

function LocationsPageInner(): ReactNode {
  const [profile, setProfile] = useState<VendorProfile | null>(null);
  const [locations, setLocations] = useState<VendorLocation[]>([]);
  const [coverage, setCoverage] = useState<ServiceArea[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [busyLocationId, setBusyLocationId] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState<LocationFormState>(EMPTY_FORM);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const profiles = await TenantWorkspaceApi.listVendorProfiles();
      const own = profiles[0] ?? null;
      setProfile(own);
      if (!own) {
        setLocations([]);
        return;
      }
      const [rows, areas] = await Promise.all([
        TenantWorkspaceApi.listLocations(own.id),
        TenantWorkspaceApi.listServiceAreas(),
      ]);
      setLocations(rows);
      setCoverage(areas);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load locations'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!profile) {
      setError(new Error('This tenant has no business profile to attach a location to'));
      return;
    }
    const built = buildLocationRequest(formData);
    if ('message' in built) {
      setError(new Error(built.message));
      return;
    }

    setIsCreating(true);
    setError(null);
    setNotice(null);
    try {
      await TenantWorkspaceApi.createLocation(profile.id, built.request);
      setShowCreateForm(false);
      setFormData(EMPTY_FORM);
      setNotice('Location created. Publish it to make it publicly discoverable.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to create location'));
    } finally {
      setIsCreating(false);
    }
  };

  const handlePublish = async (location: VendorLocation) => {
    if (!profile) return;
    setBusyLocationId(location.id);
    setError(null);
    setNotice(null);
    try {
      const address = location.privateAddress;
      const displayRegion = address?.region ?? address?.city ?? 'Region';
      await TenantWorkspaceApi.publishLocation(
        profile.id,
        location.id,
        {
          displayRegion,
          approximateCoordinates: address?.coordinates ?? undefined,
        },
        location.version,
      );
      setNotice(`"${location.label}" is published — it can now be discovered.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to publish location'));
    } finally {
      setBusyLocationId(null);
    }
  };

  /**
   * Completes or corrects a location.
   *
   * A location created before the platform recorded coordinates — or one whose
   * operator mistyped an address — is INCOMPLETE: discovery cannot place it and
   * a radius coverage is refused for it. This is the path that completes it.
   */
  const handleEdit = async (location: VendorLocation, input: UpdateLocationRequest) => {
    setBusyLocationId(location.id);
    setError(null);
    setNotice(null);
    try {
      await TenantWorkspaceApi.updateLocation(location.id, input);
      setNotice(`"${location.label}" is updated.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update location'));
    } finally {
      setBusyLocationId(null);
    }
  };

  const handleCoverage = async (
    location: VendorLocation,
    input: {
      coverageType: CoverageType;
      postalCode: string;
      radiusMeters: number | null;
      modes: FulfillmentMode[];
    },
  ) => {
    setBusyLocationId(location.id);
    setError(null);
    setNotice(null);
    try {
      const result = await TenantWorkspaceApi.assignServiceArea({
        locationId: location.id,
        coverageType: input.coverageType,
        postalCode: input.coverageType === 'postal' ? input.postalCode : null,
        radiusMeters: input.coverageType === 'radius' ? input.radiusMeters : null,
        modes: input.modes,
      });
      setNotice(`Coverage saved for "${location.label}" (${input.coverageType}).`);
      // The collection is read back from the API; the id the write returned is
      // kept so the operator sees what they just configured even if the API's
      // collection view is behind.
      setCoverage((previous) => [
        {
          id: result.id,
          vendorLocationId: location.id,
          coverageType: input.coverageType,
          postalCode: input.coverageType === 'postal' ? input.postalCode : null,
          radiusMeters: input.coverageType === 'radius' ? input.radiusMeters : null,
          country: location.privateAddress?.country ?? 'IN',
          fulfillmentModes: input.modes,
          status: 'active',
        },
        ...previous.filter((area) => area.id !== result.id),
      ]);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to save coverage'));
    } finally {
      setBusyLocationId(null);
    }
  };

  const errorMessage = error?.message ?? 'An unknown error occurred';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error && locations.length === 0) {
    return <ErrorState title="Could not load locations" message={errorMessage} onRetry={load} />;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FiMapPin className="h-5 w-5" />
            Locations & Branches
          </h2>
          <p className="text-sm text-content-muted mt-1">
            A location needs a city and a coordinate before it can serve anyone. Publishing it is
            what makes it publicly discoverable; coverage is what tells the platform where it
            serves.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={load} disabled={isLoading}>
            <FiRefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateForm(true)}>
            <FiPlus className="h-4 w-4 mr-2" />
            Add Location
          </Button>
        </div>
      </div>

      {error && <ErrorState title="Operation failed" message={errorMessage} />}
      {notice && (
        <p className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-content">
          {notice}
        </p>
      )}

      {!profile && (
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <p className="text-sm text-content flex items-center gap-2">
            <FiAlertCircle className="h-4 w-4" />
            This tenant has no business profile, so there is nowhere to attach a location.
          </p>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <section className="rounded-lg border border-border p-6 space-y-4 bg-muted/30">
          <h3 className="text-lg font-medium">Add New Location</h3>

          <Input
            label="Label *"
            value={formData.label}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setFormData((prev) => ({ ...prev, label: value }));
            }}
            placeholder="e.g., Main Store, Koramangala Branch"
            maxLength={80}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Country *"
              value={formData.country}
              onChange={(e) => {
                const value = e.currentTarget.value.toUpperCase();
                setFormData((prev) => ({ ...prev, country: value }));
              }}
              maxLength={2}
              placeholder="IN"
            />
            <Input
              label="City"
              value={formData.city}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setFormData((prev) => ({ ...prev, city: value }));
              }}
              placeholder="Bengaluru"
              maxLength={120}
            />
            <Input
              label="Region"
              value={formData.region}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setFormData((prev) => ({ ...prev, region: value }));
              }}
              placeholder="Karnataka"
              maxLength={120}
              hint="Shown to customers as the display region once the location is published."
            />
            <Input
              label="Postal Code"
              value={formData.postalCode}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setFormData((prev) => ({ ...prev, postalCode: value }));
              }}
              placeholder="560034"
              maxLength={16}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-content">Address Lines *</label>
            {formData.addressLines.map((line, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  label={`Address line ${index + 1}`}
                  value={line}
                  onChange={(e) => {
                    const newLines = [...formData.addressLines];
                    newLines[index] = e.currentTarget.value;
                    setFormData((prev) => ({ ...prev, addressLines: newLines }));
                  }}
                  placeholder={`Address line ${index + 1}`}
                  className="flex-1"
                />
                {formData.addressLines.length < 5 && index === formData.addressLines.length - 1 && (
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, addressLines: [...prev.addressLines, ''] }))
                    }
                  >
                    <FiPlus className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Latitude"
              value={formData.latitude}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setFormData((prev) => ({ ...prev, latitude: value }));
              }}
              placeholder="12.9352"
              inputMode="decimal"
              hint="Discovery places a vendor by this point, and a radius coverage needs it."
            />
            <Input
              label="Longitude"
              value={formData.longitude}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setFormData((prev) => ({ ...prev, longitude: value }));
              }}
              placeholder="77.6245"
              inputMode="decimal"
            />
            <Input
              label="Timezone"
              value={formData.timezone}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setFormData((prev) => ({ ...prev, timezone: value }));
              }}
              placeholder="Asia/Kolkata"
              maxLength={64}
            />
          </div>

          <div className="flex gap-2 pt-4 border-t border-border">
            <Button variant="secondary" onClick={() => setShowCreateForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={isCreating} disabled={isCreating}>
              <FiPlus className="h-4 w-4 mr-2" />
              Create Location
            </Button>
          </div>
        </section>
      )}

      {/* Locations List */}
      <section className="rounded-lg border border-border overflow-hidden">
        {locations.length === 0 ? (
          <div className="p-12 text-center text-content-muted">
            <FiMapPin className="h-12 w-12 mx-auto mb-4 text-content-muted/50" />
            <p className="text-lg">No locations yet</p>
            <p className="text-sm mt-1">Add your first location to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {locations.map((location) => (
              <LocationRow
                key={location.id}
                location={location}
                coverage={coverage.filter((area) => area.vendorLocationId === location.id)}
                isBusy={busyLocationId === location.id}
                onPublish={handlePublish}
                onEdit={handleEdit}
                onCoverage={handleCoverage}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function LocationsPage(): ReactNode {
  return <LocationsPageInner />;
}

interface LocationRowProps {
  location: VendorLocation;
  coverage: ServiceArea[];
  isBusy: boolean;
  onPublish: (location: VendorLocation) => void;
  onEdit: (location: VendorLocation, input: UpdateLocationRequest) => void;
  onCoverage: (
    location: VendorLocation,
    input: {
      coverageType: CoverageType;
      postalCode: string;
      radiusMeters: number | null;
      modes: FulfillmentMode[];
    },
  ) => void;
}

function LocationRow({
  location,
  coverage,
  isBusy,
  onPublish,
  onEdit,
  onCoverage,
}: LocationRowProps) {
  const [showCoverageForm, setShowCoverageForm] = useState(false);
  const [coverageType, setCoverageType] = useState<CoverageType>('radius');
  const [postalCode, setPostalCode] = useState(() => {
    const result = location?.privateAddress?.postalCode ?? '';
    return result;
  });
  const [radiusMeters, setRadiusMeters] = useState('3000');
  const [modes, setModes] = useState<FulfillmentMode[]>(['delivery']);
  const [formError, setFormError] = useState<string | null>(null);

  // The edit form opens with what the location has, so the operator corrects
  // one field rather than retyping the record.
  const [showEditForm, setShowEditForm] = useState(false);
  const [editLabel, setEditLabel] = useState(location.label);
  const [editCity, setEditCity] = useState(location.privateAddress?.city ?? '');
  const [editPostalCode, setEditPostalCode] = useState(location.privateAddress?.postalCode ?? '');
  const [editLatitude, setEditLatitude] = useState(
    location.privateAddress?.coordinates
      ? String(location.privateAddress.coordinates.latitude)
      : '',
  );
  const [editLongitude, setEditLongitude] = useState(
    location.privateAddress?.coordinates
      ? String(location.privateAddress.coordinates.longitude)
      : '',
  );
  const [editError, setEditError] = useState<string | null>(null);

  /**
   * Turns the edit form into the update request, or names what is wrong.
   *
   * A coordinate is a PAIR: the API refuses half of one, so the UI refuses to
   * send it — the operator gets the reason here rather than a 422.
   */
  const submitEdit = () => {
    setEditError(null);
    const label = editLabel.trim();
    if (!label) {
      setEditError('A label is required');
      return;
    }
    const hasLatitude = editLatitude.trim() !== '';
    const hasLongitude = editLongitude.trim() !== '';
    if (hasLatitude !== hasLongitude) {
      setEditError('Latitude and longitude come as a pair — fill in both, or neither');
      return;
    }
    const request: UpdateLocationRequest = {
      label,
      ...(editCity.trim() ? { city: editCity.trim() } : {}),
      ...(editPostalCode.trim() ? { postalCode: editPostalCode.trim() } : {}),
    };
    if (hasLatitude && hasLongitude) {
      const latitude = Number(editLatitude);
      const longitude = Number(editLongitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        setEditError('Latitude and longitude must be numbers');
        return;
      }
      if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
        setEditError('Latitude must be within -90..90 and longitude within -180..180');
        return;
      }
      request.latitude = latitude;
      request.longitude = longitude;
    }
    onEdit(location, request);
    setShowEditForm(false);
  };

  const statusConfig =
    PUBLICATION_STATUSES.find((s) => s.value === location.publicationStatus) ??
    PUBLICATION_STATUSES[1]!;
  const address = location?.privateAddress;
  const coordinates = address?.coordinates;

  const submitCoverage = () => {
    setFormError(null);
    if (modes.length === 0) {
      setFormError('Choose at least one fulfilment mode');
      return;
    }
    if (coverageType === 'postal' && postalCode.trim() === '') {
      setFormError('A postal coverage needs a postal code');
      return;
    }
    if (coverageType === 'radius') {
      const radius = Number(radiusMeters);
      if (!Number.isInteger(radius) || radius <= 0 || radius > MAX_RADIUS_METERS) {
        setFormError(`The radius must be a whole number of metres, up to ${MAX_RADIUS_METERS}`);
        return;
      }
      if (!coordinates) {
        setFormError('A radius coverage needs a location that has coordinates');
        return;
      }
    }
    onCoverage(location, {
      coverageType,
      postalCode: postalCode.trim(),
      radiusMeters: coverageType === 'radius' ? Number(radiusMeters) : null,
      modes,
    });
    setShowCoverageForm(false);
  };

  return (
    <div className="p-4 hover:bg-muted/30 transition-colors space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-4 flex-1">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-muted text-content-muted">
            <FiMapPin className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-content truncate">{location.label}</h4>
              <Badge tone={statusConfig.tone}>{statusConfig.label}</Badge>
            </div>
            <p className="text-sm text-content-muted font-mono">{location.id.slice(0, 8)}…</p>
            <p className="text-sm text-content-muted">
              {address
                ? [address.city, address.region, address.country].filter(Boolean).join(', ')
                : 'Address not loaded'}
            </p>
            <p className="text-sm text-content-muted">
              {address ? address.addressLines.join(', ') : '—'}
            </p>
            <div className="flex items-center gap-3 mt-1 text-xs text-content-muted">
              <span>
                {coordinates
                  ? `${coordinates.latitude}, ${coordinates.longitude}`
                  : 'No coordinates'}
              </span>
              <span>•</span>
              <span>{location.timezone}</span>
              <span>• v{location.version}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {location.publicationStatus !== 'published' && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onPublish(location)}
              disabled={isBusy}
            >
              <FiSend className="h-3 w-3 mr-1" /> Publish
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowCoverageForm((open) => !open)}
            disabled={isBusy}
          >
            <FiTarget className="h-3 w-3 mr-1" /> Coverage
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowEditForm((open) => !open)}
            disabled={isBusy}
          >
            <FiEdit2 className="h-3 w-3 mr-1" /> Edit
          </Button>
        </div>
      </div>

      {showEditForm && (
        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
          <h5 className="text-sm font-medium text-content">Complete {location.label}</h5>
          <p className="text-xs text-content-muted">
            A location needs a city and a coordinate before discovery can place it. A radius
            coverage is refused for a location that has none.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Label *"
              value={editLabel}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setEditLabel(value);
              }}
              maxLength={80}
            />
            <Input
              label="City"
              value={editCity}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setEditCity(value);
              }}
              maxLength={120}
            />
            <Input
              label="Postal Code"
              value={editPostalCode}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setEditPostalCode(value);
              }}
              maxLength={16}
            />
            <Input
              label="Latitude"
              value={editLatitude}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setEditLatitude(value);
              }}
              inputMode="decimal"
            />
            <Input
              label="Longitude"
              value={editLongitude}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setEditLongitude(value);
              }}
              inputMode="decimal"
            />
          </div>

          {editError && <p className="text-xs text-critical">{editError}</p>}

          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowEditForm(false)}>
              Cancel
            </Button>
            <Button onClick={submitEdit} disabled={isBusy}>
              Save Changes
            </Button>
          </div>
        </div>
      )}

      {coverage.length > 0 && (
        <ul className="flex flex-wrap gap-2 text-xs">
          {coverage.map((area) => (
            <li key={area.id}>
              <Badge tone="neutral">
                {area.coverageType === 'postal'
                  ? `Postal ${area.postalCode ?? '—'}`
                  : `Radius ${area.radiusMeters ?? '—'} m`}{' '}
                · {area.fulfillmentModes.join(', ')}
              </Badge>
            </li>
          ))}
        </ul>
      )}

      {showCoverageForm && (
        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
          <h5 className="text-sm font-medium text-content">Coverage for {location.label}</h5>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label
                className="text-sm font-medium text-content"
                htmlFor={`coverage-type-${location.id}`}
              >
                Coverage type
              </label>
              <select
                id={`coverage-type-${location.id}`}
                className="rounded-md border border-border bg-background px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={coverageType}
                onChange={(e) => setCoverageType(e.currentTarget.value as CoverageType)}
              >
                {COVERAGE_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {coverageType === 'postal' ? (
              <Input
                label="Postal code"
                value={postalCode}
                onChange={(e) => setPostalCode(e.currentTarget.value)}
                placeholder="560034"
                maxLength={16}
              />
            ) : (
              <Input
                label="Radius (metres)"
                value={radiusMeters}
                onChange={(e) => setRadiusMeters(e.currentTarget.value)}
                inputMode="numeric"
                hint={
                  coordinates
                    ? `Centred on ${coordinates.latitude}, ${coordinates.longitude}.`
                    : 'This location has no coordinates, so a radius coverage will be refused.'
                }
              />
            )}
          </div>

          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium text-content">Fulfilment modes</legend>
            {FULFILMENT_MODES.map((mode) => (
              <label key={mode.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={modes.includes(mode.value)}
                  onChange={(e) => {
                    // The synthetic event is dead by the time a queued updater
                    // runs, so the flag is read HERE and the updater closes
                    // over the value, never over the event.
                    const checked = e.currentTarget.checked;
                    setModes((previous) =>
                      checked
                        ? [...previous, mode.value]
                        : previous.filter((m) => m !== mode.value),
                    );
                  }}
                  className="rounded border-border text-primary focus:ring-primary"
                />
                <span className="text-sm text-content">{mode.label}</span>
              </label>
            ))}
          </fieldset>

          {formError && <p className="text-xs text-critical">{formError}</p>}

          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowCoverageForm(false)}>
              Cancel
            </Button>
            <Button onClick={submitCoverage} disabled={isBusy}>
              Save Coverage
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
